import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  ShoppingBag, GraduationCap, Briefcase, Check, Palette, Layout, 
  Globe, Shield, Lock, CheckCircle2, Settings, Layers 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const TEMPLATE_ICONS = {
  commerce: ShoppingBag,
  course: GraduationCap,
  high_ticket: Briefcase
};

const TEMPLATE_COLORS = {
  commerce: 'from-blue-500 to-indigo-600',
  course: 'from-purple-500 to-pink-600',
  high_ticket: 'from-amber-500 to-orange-600'
};

export default function StoreTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState('');
  const [activeModules, setActiveModules] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [applySettings, setApplySettings] = useState({ apply_pages: true, apply_settings: true });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, modulesRes, domainsRes] = await Promise.all([
        api.get('/tenant/store-templates'),
        api.get('/tenant/active-modules'),
        api.get('/tenant/domains-with-ssl')
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCurrentTemplate(templatesRes.data.current_template);
      setActiveModules(modulesRes.data.active_modules || []);
      setDomains(domainsRes.data.domains || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    try {
      await api.post('/tenant/store-templates/apply', {
        template_id: selectedTemplate.id,
        color_scheme_index: selectedColorIndex,
        apply_pages: applySettings.apply_pages,
        apply_settings: applySettings.apply_settings
      });
      toast({ title: 'Berhasil', description: `Template ${selectedTemplate.name} berhasil diterapkan` });
      setShowApplyDialog(false);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menerapkan template', variant: 'destructive' });
    }
  };

  const toggleModule = async (moduleId, enabled) => {
    try {
      await api.put('/tenant/toggle-module', { module_id: moduleId, enabled });
      setActiveModules(prev => 
        enabled ? [...prev, moduleId] : prev.filter(m => m !== moduleId)
      );
      toast({ title: 'Berhasil', description: `Modul ${moduleId} ${enabled ? 'diaktifkan' : 'dinonaktifkan'}` });
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengubah modul', variant: 'destructive' });
    }
  };

  const enableSSL = async (domainId) => {
    try {
      await api.post(`/tenant/domains/${domainId}/enable-ssl`);
      toast({ title: 'Berhasil', description: 'SSL berhasil diaktifkan' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal mengaktifkan SSL', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="store-templates-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Template & Pengaturan Store</h1>
        <p className="text-muted-foreground">Pilih template, kelola modul, dan konfigurasi domain</p>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates"><Layout className="h-4 w-4 mr-2" />Template Store</TabsTrigger>
          <TabsTrigger value="modules"><Layers className="h-4 w-4 mr-2" />Modul Aktif</TabsTrigger>
          <TabsTrigger value="domains"><Globe className="h-4 w-4 mr-2" />Domain & SSL</TabsTrigger>
        </TabsList>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templates.map((template) => {
              const Icon = TEMPLATE_ICONS[template.id] || ShoppingBag;
              const isActive = currentTemplate === template.id;
              
              return (
                <Card 
                  key={template.id} 
                  className={`relative overflow-hidden transition-all ${isActive ? 'ring-2 ring-primary' : 'hover:shadow-lg'}`}
                  data-testid={`template-card-${template.id}`}
                >
                  {isActive && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-emerald-500">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Aktif
                      </Badge>
                    </div>
                  )}
                  <div className={`h-24 bg-gradient-to-r ${TEMPLATE_COLORS[template.id] || 'from-gray-500 to-gray-600'} flex items-center justify-center`}>
                    <Icon className="h-12 w-12 text-white/90" />
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle>{template.name}</CardTitle>
                    <CardDescription className="text-sm">{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Modul Default:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.default_modules?.slice(0, 5).map((mod) => (
                            <Badge key={mod} variant="outline" className="text-xs">{mod}</Badge>
                          ))}
                          {template.default_modules?.length > 5 && (
                            <Badge variant="outline" className="text-xs">+{template.default_modules.length - 5}</Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Skema Warna:</p>
                        <div className="flex gap-1">
                          {template.color_schemes?.slice(0, 6).map((scheme, i) => (
                            <div 
                              key={i}
                              className="w-6 h-6 rounded-full border-2 border-white shadow"
                              style={{ backgroundColor: scheme.primary }}
                              title={scheme.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full"
                      variant={isActive ? 'outline' : 'default'}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setSelectedColorIndex(0);
                        setShowApplyDialog(true);
                      }}
                      data-testid={`apply-template-${template.id}`}
                    >
                      {isActive ? 'Konfigurasi Ulang' : 'Gunakan Template'}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* MODULES TAB */}
        <TabsContent value="modules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Modul Store Aktif</CardTitle>
              <CardDescription>Aktifkan atau nonaktifkan modul sesuai kebutuhan bisnis Anda</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { id: 'products_physical', name: 'Produk Fisik', desc: 'Jual produk fisik dengan stok dan pengiriman' },
                  { id: 'products_digital', name: 'Produk Digital', desc: 'Jual file digital dengan proteksi download' },
                  { id: 'lms', name: 'LMS / Course', desc: 'Buat dan jual kursus online' },
                  { id: 'membership', name: 'Membership', desc: 'Buat paket membership premium' },
                  { id: 'donation', name: 'Donasi', desc: 'Terima donasi dan galang dana' },
                  { id: 'ppob', name: 'PPOB', desc: 'Jual pulsa, token listrik, dll' },
                  { id: 'payment_plan', name: 'Payment Plan', desc: 'DP, cicilan, dan termin pembayaran' },
                  { id: 'affiliate_basic', name: 'Affiliate', desc: 'Program afiliasi untuk pelanggan' },
                  { id: 'checkout', name: 'Checkout', desc: 'Sistem checkout dan pembayaran' },
                  { id: 'cod', name: 'COD', desc: 'Cash on delivery' },
                  { id: 'promo', name: 'Promo', desc: 'Kupon dan diskon' },
                  { id: 'certificates', name: 'Sertifikat', desc: 'Sertifikat kursus otomatis' },
                ].map((mod) => (
                  <div key={mod.id} className="flex items-start justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{mod.name}</p>
                      <p className="text-sm text-muted-foreground">{mod.desc}</p>
                    </div>
                    <Switch
                      checked={activeModules.includes(mod.id)}
                      onCheckedChange={(v) => toggleModule(mod.id, v)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOMAINS TAB */}
        <TabsContent value="domains" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-500" />
                Domain & SSL
              </CardTitle>
              <CardDescription>Kelola domain dan sertifikat SSL untuk keamanan store</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {domains.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Belum ada custom domain. Tambahkan di menu Domain Manager.
                  </div>
                ) : (
                  domains.map((domain) => (
                    <div key={domain.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{domain.domain}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={domain.status === 'verified' || domain.status === 'active' ? 'default' : 'secondary'}>
                              {domain.status}
                            </Badge>
                            {domain.is_primary && <Badge variant="outline">Primary</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            {domain.ssl_enabled ? (
                              <>
                                <Lock className="h-4 w-4 text-emerald-500" />
                                <span className="text-sm text-emerald-600">SSL Aktif</span>
                              </>
                            ) : (
                              <>
                                <Shield className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">SSL Nonaktif</span>
                              </>
                            )}
                          </div>
                          {domain.ssl_expires && (
                            <p className="text-xs text-muted-foreground">
                              Expired: {new Date(domain.ssl_expires).toLocaleDateString('id-ID')}
                            </p>
                          )}
                        </div>
                        {!domain.ssl_enabled && (domain.status === 'verified' || domain.status === 'active') && (
                          <Button size="sm" onClick={() => enableSSL(domain.id)}>
                            Aktifkan SSL
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* APPLY TEMPLATE DIALOG */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Terapkan Template: {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-6">
              <div>
                <Label className="mb-2 block">Pilih Skema Warna</Label>
                <div className="grid grid-cols-3 gap-3">
                  {selectedTemplate.color_schemes?.map((scheme, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedColorIndex(i)}
                      className={`p-3 border rounded-lg text-left transition-all ${selectedColorIndex === i ? 'ring-2 ring-primary' : 'hover:bg-muted'}`}
                    >
                      <div 
                        className="w-full h-8 rounded mb-2"
                        style={{ background: `linear-gradient(135deg, ${scheme.primary}, ${scheme.secondary || scheme.primary})` }}
                      />
                      <p className="text-xs font-medium truncate">{scheme.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Buat Halaman Default</Label>
                    <p className="text-xs text-muted-foreground">Buat halaman seperti Home, About, Contact, dll</p>
                  </div>
                  <Switch 
                    checked={applySettings.apply_pages}
                    onCheckedChange={(v) => setApplySettings({ ...applySettings, apply_pages: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Terapkan Pengaturan Default</Label>
                    <p className="text-xs text-muted-foreground">Terapkan konfigurasi modul dari template</p>
                  </div>
                  <Switch 
                    checked={applySettings.apply_settings}
                    onCheckedChange={(v) => setApplySettings({ ...applySettings, apply_settings: v })}
                  />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Perhatian:</strong> Menerapkan template akan mengubah tampilan dan modul aktif store Anda. 
                  Halaman yang sudah ada tidak akan dihapus.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>Batal</Button>
            <Button onClick={handleApplyTemplate}>Terapkan Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
